/** Realm role a global administrator carries; see the backend's `Roles.cs`. */
export const GLOBAL_ADMIN_ROLE = 'GlobalAdminRole';

/** Namespaced claims the API mints into the access token (`CustomClaim.cs`). */
const ORGANIZATION_CLAIM = 'https://aiutante.com/organization';
const ORGANIZATION_ADMIN_CLAIM = 'https://aiutante.com/organization_admin';

interface JwtPayload {
    realm_access?: {
        roles?: string[];
    };
    [claim: string]: unknown;
}

/** Decode the payload of a JWT without verifying its signature. */
export function decodeJwt(token?: string | null): JwtPayload | null {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json) as JwtPayload;
    } catch {
        return null;
    }
}

const claimValue = (payload: JwtPayload | null, claim: string): string | null => {
    const value = payload?.[claim];
    return typeof value === 'string' && value.length > 0 ? value : null;
};

/** True when the access token carries the GlobalAdminRole realm role. */
export function hasGlobalAdminRole(token?: string | null): boolean {
    const payload = decodeJwt(token);
    return payload?.realm_access?.roles?.includes(GLOBAL_ADMIN_ROLE) ?? false;
}

/**
 * True when the token identifies an administrator of one organization — the
 * same pair of claims the API's `RequireOrganizationAdmin` policy demands, so
 * a token this accepts is a token every screen here can actually use.
 */
export function hasOrganizationAdminClaims(token?: string | null): boolean {
    const payload = decodeJwt(token);
    return claimValue(payload, ORGANIZATION_ADMIN_CLAIM) !== null
        && claimValue(payload, ORGANIZATION_CLAIM) !== null;
}

/**
 * Whether this token may use the app at all.
 *
 * Organization admins only: a global administrator has their own console and
 * is deliberately turned away here, even when the same account also carries
 * the organization claims.
 */
export function isAllowedToSignIn(token?: string | null): boolean {
    return hasOrganizationAdminClaims(token) && !hasGlobalAdminRole(token);
}
